/**
 * MIKIWAME デザイントークン
 *
 * 目的: デザインモック (design/final) と整合する「コンテンツ用アクセント／中立色」を一元管理する。
 * 既存の MUI テーマ (primary #667eea / secondary #764ba2 = ブランド紫・ヘッダー) は変更しない。
 * これは MUI theme を置き換えるものではなく、各画面で意匠を当てる際に参照する補助トークン。
 *
 * 値はモック frame.js のCSS変数に合わせている (teal #0d9488 / indigo #5b56e0 等)。
 * light / dark 両方を必ず持ち、ダークモードを壊さないようにする。
 */

/** 判定の状態。画面の比較・集計はこの5つだけで行い、保存されている日本語の文字は直接見ない。
 *  error は「調べた結果わからなかった（unverifiable）」ではなく「処理が失敗した」。別物として数える。 */
export type VerdictKey = 'accurate' | 'inaccurate' | 'partial' | 'unverifiable' | 'error';

export interface DesignTokens {
  /** 主要テキスト色 */
  ink: string;
  /** 補助テキスト色 */
  muted: string;
  /** 薄いテキスト色 (キャプション等) */
  faint: string;
  /** 枠線色 */
  line: string;
  /** パネル/カードの背景 */
  paper: string;
  /** アクセント (CTA・検証アクション等) */
  teal: string;
  /** アクセント (補助・ユーザー系) */
  indigo: string;
  /** ヒーローグラデーション開始色 */
  heroFrom: string;
  /** ヒーローグラデーション終了色 */
  heroTo: string;
  /** 判定(4分類)ごとの文字色とチップ背景(tint) */
  verdict: Record<VerdictKey, { color: string; tint: string }>;
}

export const lightTokens: DesignTokens = {
  ink: '#1b2030',
  muted: '#5c6275',
  faint: '#9098ab',
  line: 'rgba(20, 24, 40, 0.09)',
  paper: '#ffffff',
  teal: '#0d9488',
  indigo: '#5b56e0',
  heroFrom: '#eef0fb',
  heroTo: '#e7f6f1',
  verdict: {
    accurate: { color: '#16a34a', tint: '#e7f6ed' },
    inaccurate: { color: '#dc2626', tint: '#fdeaea' },
    partial: { color: '#e08600', tint: '#fdf2e0' },
    unverifiable: { color: '#64748b', tint: '#eef1f5' },
    // 検証不可（灰）と紛れないよう、一段濃い石色にする
    error: { color: '#475569', tint: '#e8ebef' },
  },
};

export const darkTokens: DesignTokens = {
  ink: '#e8eaf2',
  muted: '#aab2c5',
  faint: '#7e87a0',
  line: 'rgba(255, 255, 255, 0.12)',
  paper: 'rgba(255, 255, 255, 0.05)',
  teal: '#2dd4bf',
  indigo: '#818cf8',
  heroFrom: 'rgba(102, 126, 234, 0.18)',
  heroTo: 'rgba(13, 148, 136, 0.16)',
  verdict: {
    accurate: { color: '#34d399', tint: 'rgba(16, 163, 74, 0.18)' },
    inaccurate: { color: '#f87171', tint: 'rgba(220, 38, 38, 0.18)' },
    partial: { color: '#fbbf24', tint: 'rgba(245, 158, 11, 0.18)' },
    unverifiable: { color: '#94a3b8', tint: 'rgba(100, 116, 139, 0.20)' },
    error: { color: '#cbd5e1', tint: 'rgba(148, 163, 184, 0.26)' },
  },
};

// ----- 判定ラベル <-> キー <-> シール画像 のヘルパー -----

export const VERDICT_LABELS: Record<VerdictKey, string> = {
  accurate: '正確',
  inaccurate: '不正確',
  partial: '部分的に正確',
  unverifiable: '検証不可能',
  error: '検証エラー',
};

/**
 * 保存されている判定文字列を VerdictKey に正規化する。ここが唯一の入口で、
 * これより先のコードは日本語の文字を見ない。
 *
 * AI の出力は揺れるため（'正確' / 'ACCURATE' / '正しい' …）、バックエンドが
 * 4値に寄せてから保存している。それでも処理失敗時の '検証エラー' のように
 * 判定ではない値が入るので、ここで受け止める。
 *
 * 判定順に意味がある。'正確' は '不正確' にも '部分的に正確' にも含まれるため、
 * 狭いものから先に見ないと取り違える。
 */
export const verdictKeyFromLabel = (label?: string | null): VerdictKey => {
  // 未検証のものは空で来る。これは失敗ではないので error にはしない。
  if (!label) return 'unverifiable';
  if (label.includes('エラー')) return 'error';
  if (label.includes('部分')) return 'partial';
  if (label.includes('不正確')) return 'inaccurate';
  if (label.includes('正確') || label === '正しい') return 'accurate';
  if (
    label.includes('検証不可') ||
    label.includes('不明') ||
    label.includes('判断不能') ||
    label.includes('確認不可')
  ) {
    return 'unverifiable';
  }
  // 想定外の値。黙って検証不可に混ぜると、画面の内訳と合計が合わなくなる（#144）。
  return 'error';
};

/** 判定キーに対応するシール画像のパス (public/assets/verdict) */
const SEAL_SRC: Record<VerdictKey, string> = {
  accurate: '/assets/verdict/accurate.png',
  inaccurate: '/assets/verdict/inaccurate.png',
  partial: '/assets/verdict/partial.png',
  unverifiable: '/assets/verdict/unverifiable.png',
  error: '/assets/verdict/error.svg',
};
export const verdictSealSrc = (key: VerdictKey): string => SEAL_SRC[key];

/** 判定文字列から直接シール画像パスを得るショートカット */
export const verdictSealFromLabel = (label?: string | null): string =>
  verdictSealSrc(verdictKeyFromLabel(label));
